import { useEffect, useState } from 'react'
import { Link, NavLink, Outlet, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useOrg } from '@/stores/org'
import { OrgRouteGate } from '@/components/OrgRouteGate'
import { IconButton } from '@/components/ui/icon-button'
import { ThemeToggle } from '@/components/ui/theme-toggle'
import { LanguageSwitch } from '@/components/LanguageSwitch'
import { MOBILE_OVERFLOW, MOBILE_PRIMARY, NAV_GROUPS, NAV_ITEMS } from '@/lib/nav'
import { useQuery } from '@tanstack/react-query'
import { announcementsKeys, announcementTitle, announcementActionPath, fetchPublicAnnouncements } from '@/lib/resources/announcements'
import { cn } from '@/lib/utils'
import { SK } from '@/lib/storageKeys'
import { ChevronRight, MoreHorizontal, X } from 'lucide-react'
import { TopBar } from '@/components/layout/TopBar'

const nav = NAV_ITEMS

const dashboardNav = nav.find((n) => n.to === '/dashboard')!
const byTo: Record<string, (typeof nav)[number]> = Object.fromEntries(nav.map((n) => [n.to, n]))

// enterprise sharp nav — 0 radius, hairline border-enterprise-border, indigo focus
const pillLinkBase =
  'inline-flex h-10 items-center border border-transparent px-3 text-sm font-medium tracking-tight motion-transition-fast focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-enterprise-indigo focus-visible:ring-offset-2 focus-visible:ring-offset-enterprise-bg'
const pillRailBase =
  'flex h-10 items-center justify-between border border-transparent px-3 text-sm font-medium tracking-tight motion-transition-fast focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-enterprise-indigo focus-visible:ring-offset-2 focus-visible:ring-offset-enterprise-bg'

export function Layout() {
  const { t, i18n } = useTranslation()
  const tr = t as unknown as (k: string, o?: Record<string, unknown>) => string
  const { refresh } = useOrg()
  const [mobileMoreOpen, setMobileMoreOpen] = useState(false)
  // 侧栏分组折叠 — localStorage 持久化，默认全部展开
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>(() => {
    try {
      return JSON.parse(localStorage.getItem(SK.navCollapsed) ?? '{}')
    } catch {
      return {}
    }
  })
  const toggleGroup = (key: string) =>
    setCollapsed((c) => {
      const next = { ...c, [key]: !c[key] }
      try {
        localStorage.setItem(SK.navCollapsed, JSON.stringify(next))
      } catch {
        // 隐私模式等写失败 — 折叠仅存活于本会话，可接受
      }
      return next
    })
  const location = useLocation()

  useEffect(() => {
    void refresh()
  }, [refresh])

  // 路由级标签页标题 — 多部署/多训练并行时浏览器历史才可辨。
  useEffect(() => {
    const seg = '/' + (location.pathname.split('/')[1] ?? '')
    const item = byTo[seg]
    document.title = item ? `${t(item.labelKey)} · Togence Console` : 'Togence Console'
  }, [location.pathname, t])

  // 移动端「更多」导航 — Escape / 外部点击关闭，与 dropdown-menu 同一机制
  useEffect(() => {
    if (!mobileMoreOpen) return
    function onDown(e: MouseEvent) {
      const t2 = e.target as HTMLElement | null
      if (!t2?.closest('[data-mobile-more]')) setMobileMoreOpen(false)
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setMobileMoreOpen(false)
    }
    document.addEventListener('mousedown', onDown)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onDown)
      document.removeEventListener('keydown', onKey)
    }
  }, [mobileMoreOpen])

  // 底部栏放不下的入口收进“更多” — 集合由 nav.ts manifest 派生，nav.test.ts 断言全覆盖。
  const mobilePrimary = MOBILE_PRIMARY
  const mobileOverflow = MOBILE_OVERFLOW
  // 当前页收进“更多”时底栏仍要给归属指示 — 与 NavLink 默认前缀段匹配规则一致。
  const mobileOverflowActive = mobileOverflow.some(
    (n) => location.pathname === n.to || location.pathname.startsWith(`${n.to}/`),
  )

  // 置顶公告横幅 —— 只有 pinned 条目配得上 Layout 级打断（公告板的普通内容住
  // Dashboard 区块/Landing 顶条）。关闭态存公告 id（localStorage）：新置顶公告
  // 仍会冒出来，关过的不会再来烦。共享 announcementsKeys.public 缓存。
  const announcementsQ = useQuery({
    queryKey: announcementsKeys.public,
    queryFn: fetchPublicAnnouncements,
    staleTime: 5 * 60_000,
    retry: 1,
    refetchOnWindowFocus: false,
  })
  const pinnedAnnouncement = announcementsQ.data?.find((a) => a.pinned) ?? null
  const [bannerDismissedId, setBannerDismissedId] = useState<string | null>(() => {
    try {
      return localStorage.getItem(SK.announcementBannerDismissed)
    } catch {
      return null
    }
  })
  const bannerVisible = pinnedAnnouncement !== null && bannerDismissedId !== pinnedAnnouncement.id
  const dismissBanner = () => {
    if (!pinnedAnnouncement) return
    setBannerDismissedId(pinnedAnnouncement.id)
    try {
      localStorage.setItem(SK.announcementBannerDismissed, pinnedAnnouncement.id)
    } catch {
      // storage unavailable — the banner simply returns next session
    }
  }

  return (
    <div className="min-h-screen bg-enterprise-bg text-enterprise-text">
      <a
        href="#main"
        className="sr-only focus:not-sr-only focus:absolute focus:left-4 focus:top-4 focus:z-[60] focus:rounded-none focus:bg-enterprise-inverse focus:px-4 focus:py-2 focus:text-enterprise-on-inverse focus:outline-none focus-visible:ring-2 focus-visible:ring-enterprise-indigo focus-visible:ring-offset-2 focus-visible:ring-offset-enterprise-bg"
      >
        {t('layout.skipToContent')}
      </a>

      {/* header — enterprise 55px hairline border-enterprise-border like Landing */}
      <TopBar />

      {/* 置顶公告横幅 — pinned 条目的唯一打断面；同款 hairline/raised/mono 词汇，
          行动链接只收 console 相对路径（announcementActionPath 已过滤）。 */}
      {bannerVisible && pinnedAnnouncement ? (
        <div
          role="region"
          aria-label={t('announcements.bannerLabel')}
          className="border-b border-enterprise-border bg-enterprise-raised"
        >
          <div className="mx-auto flex w-full max-w-[1280px] min-w-0 items-center gap-3 px-4 py-2 font-mono text-xs leading-5 text-enterprise-muted sm:px-6 2xl:max-w-[1650px]">
            <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-enterprise-indigo" aria-hidden="true" />
            <span className="min-w-0 flex-1 truncate text-enterprise-text">
              {announcementTitle(pinnedAnnouncement, i18n.language ?? 'zh')}
            </span>
            {announcementActionPath(pinnedAnnouncement) ? (
              <Link
                to={announcementActionPath(pinnedAnnouncement)!}
                className="shrink-0 text-enterprise-indigo underline decoration-enterprise-dim underline-offset-4 hover:text-enterprise-text focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-enterprise-indigo"
              >
                {t('announcements.cta')}
              </Link>
            ) : null}
            <IconButton
              onClick={dismissBanner}
              size="sm"
              aria-label={t('announcements.dismiss')}
              className="shrink-0 text-enterprise-subtle hover:text-enterprise-text"
            >
              <X className="h-3.5 w-3.5" aria-hidden="true" />
            </IconButton>
          </div>
        </div>
      ) : null}


      {/* outer enterprise shell — border-enterprise-border max-w-[1280px], gap-px bg-enterprise-border */}
      <div className="mx-auto flex w-full max-w-[1280px] gap-px bg-enterprise-border border-x border-b border-enterprise-border max-lg:border-x-0 max-lg:border-b-0 2xl:max-w-[1650px]">
        {/* rail — enterprise sharp */}
        <aside className="hidden w-[220px] shrink-0 border-r border-enterprise-border bg-enterprise-bg lg:block" aria-label={t('layout.sidebar')}>
          <nav className="sticky top-[55px] max-h-[calc(100vh-55px)] overflow-y-auto p-3" aria-label={t('layout.desktopNav')}>
            <div className="space-y-4">
              {/* 总览 Dashboard solo with separation */}
              <div className="space-y-1 border-b border-enterprise-border-subtle pb-2 mb-2">
                <NavLink
                  to={dashboardNav.to}
                  end
                  className={({ isActive }) =>
                    cn(
                      pillRailBase,
                      isActive ? 'border-enterprise-border bg-enterprise-raised text-enterprise-indigo' : 'text-enterprise-muted hover:border-enterprise-border hover:text-enterprise-text',
                    )
                  }
                >
                  {({ isActive }) => (
                    <span className="flex min-w-0 items-center">
                      <span aria-hidden="true" className={cn('mr-2 h-4 w-0.5 shrink-0', isActive ? 'bg-enterprise-indigo' : 'bg-transparent')} />
                      <span className="truncate font-medium tracking-tight">{tr(dashboardNav.labelKey)}</span>
                    </span>
                  )}
                </NavLink>
              </div>

              <div className="h-px bg-enterprise-border" aria-hidden="true" />

              {NAV_GROUPS.map((group) => {
                const isCollapsed = !!collapsed[group.headingKey]
                return (
                  <div key={group.headingKey} className="space-y-2">
                    <button
                      type="button"
                      onClick={() => toggleGroup(group.headingKey)}
                      aria-expanded={!isCollapsed}
                      className="flex w-full select-none items-center justify-between px-3 pb-1 pt-1.5 font-mono text-eyebrow uppercase leading-4 text-enterprise-subtle hover:text-enterprise-muted motion-transition-fast focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-enterprise-indigo"
                    >
                      {tr(group.headingKey)}
                      <ChevronRight
                        className={cn('h-3 w-3 transition-transform motion-reduce:transition-none', !isCollapsed && 'rotate-90')}
                        aria-hidden="true"
                      />
                    </button>
                    {isCollapsed ? null : (
                      <div className="space-y-1">
                        {group.items.map((n) => (
                          <NavLink
                            key={n.to}
                            to={n.to}
                            end={n.to === '/'}
                            className={({ isActive }) =>
                              cn(
                                pillRailBase,
                                isActive ? 'border-enterprise-border bg-enterprise-raised text-enterprise-indigo' : 'text-enterprise-muted hover:border-enterprise-border hover:text-enterprise-text',
                              )
                            }
                          >
                            {({ isActive }) => (
                              <span className="flex min-w-0 items-center">
                                <span aria-hidden="true" className={cn('mr-2 h-4 w-0.5 shrink-0', isActive ? 'bg-enterprise-indigo' : 'bg-transparent')} />
                                <span className="truncate font-medium tracking-tight">{tr(n.labelKey)}</span>
                              </span>
                            )}
                          </NavLink>
                        ))}
                      </div>
                    )}
                  </div>
                )
              })}
            </div>
          </nav>
        </aside>

        {/* main — enterprise bg-enterprise-bg；flex-col + mt-auto footer 让短内容页也有页脚锚定，避免大片空黑 */}
        <main
          id="main"
          tabIndex={-1}
          className="flex min-w-0 flex-1 flex-col bg-enterprise-bg p-6 pb-[calc(4rem+env(safe-area-inset-bottom))] outline-none focus-visible:ring-2 focus-visible:ring-enterprise-indigo sm:p-8 sm:pb-[calc(4rem+env(safe-area-inset-bottom))] lg:pb-8"
          aria-label={t('layout.mainContent')}
        >
          <div className="flex-1">
            <div key={location.pathname} className="motion-route">
              {/* Org context gate — validates/reconciles ?orgId= before pages fetch */}
              <OrgRouteGate>
                <Outlet />
              </OrgRouteGate>
            </div>
          </div>
          <footer className="mt-8 flex flex-wrap items-center justify-between gap-2 border-t border-enterprise-border pt-3 font-mono text-xs text-enterprise-subtle">
            <span>Togence Console · {typeof __BUILD_ID__ === 'string' ? __BUILD_ID__ : 'dev'}</span>
            <span>© 2026 Togence</span>
          </footer>
        </main>
      </div>

      {/* bottom bar — enterprise */}
      <nav
        aria-label={t('layout.mobileNav')}
        className="fixed bottom-0 left-0 right-0 z-40 border-t border-enterprise-border bg-enterprise-bg lg:hidden"
      >
        {/* 抽屉遮罩 — DOM 序在内容条之前，压暗页面但垫在底栏/抽屉之下；点击冒泡到
            document 的 outside-down 处理器关闭抽屉（同 dropdown-menu 机制） */}
        {mobileMoreOpen ? <div aria-hidden="true" className="fixed inset-0 z-0 bg-black/40" /> : null}
        <div className="relative flex min-h-10 items-center gap-1 px-2 py-2 pb-[calc(0.5rem+env(safe-area-inset-bottom))]">
          <div className="flex min-h-10 flex-1 items-center gap-1 overflow-x-auto pr-8 scrollbar-none [-ms-overflow-style:none] [scrollbar-width:none]">
            {mobilePrimary.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.to === '/'}
                className={({ isActive }) =>
                  cn(
                    pillLinkBase + ' whitespace-nowrap px-3 py-1.5',
                    isActive
                      ? // relative z-10 — 滚到右端淡出遮罩下的 active 项仍压在 scrim 之上，保持清晰可读
                        'relative z-10 border-enterprise-indigo bg-enterprise-indigo text-white'
                      : 'border-enterprise-border bg-enterprise-bg text-enterprise-muted hover:border-enterprise-dim hover:text-enterprise-text',
                  )
                }
              >
                {tr(n.labelKey)}
              </NavLink>
            ))}
          </div>

          {mobileOverflow.length > 0 && (
            <div data-mobile-more className="relative flex shrink-0 items-center">
              {/* 滚动边缘淡出：贴在「更多」按钮左缘（right-full 跟随其本地化宽度），
                  只盖滚动区尾部 pr-8 留白，不再压住任何导航项 */}
              <div
                aria-hidden="true"
                className="pointer-events-none absolute right-full top-0 h-full w-8 bg-gradient-to-l from-enterprise-bg to-transparent"
              />
              <button
                type="button"
                aria-label={t('layout.moreNav')}
                aria-expanded={mobileMoreOpen}
                aria-current={mobileOverflowActive ? 'page' : undefined}
                onClick={() => setMobileMoreOpen((v) => !v)}
                className={cn(
                  pillLinkBase + ' gap-1.5 whitespace-nowrap px-3 py-1.5',
                  mobileOverflowActive
                    ? 'border-enterprise-indigo text-enterprise-indigo'
                    : 'border-enterprise-border bg-enterprise-bg text-enterprise-muted hover:border-enterprise-dim hover:text-enterprise-text',
                )}
              >
                <MoreHorizontal className="h-4 w-4" aria-hidden="true" />
                <span>{t('layout.more')}</span>
              </button>
              {mobileMoreOpen && (
                <div className="absolute bottom-full right-0 mb-2 flex max-h-[50vh] min-w-[160px] flex-col gap-1 overflow-auto border border-enterprise-border bg-enterprise-bg p-1 shadow-none">
                  <div className="flex items-center justify-between px-2 py-1">
                    <span className="text-xs tracking-wide text-enterprise-muted">{t('layout.more')}</span>
                    <IconButton
                      aria-label={t('layout.closeMoreNav')}
                      onClick={() => setMobileMoreOpen(false)}
                      className="rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-enterprise-indigo"
                    >
                      <X className="h-3.5 w-3.5" aria-hidden="true" />
                    </IconButton>
                  </div>
                  <div className="h-px bg-enterprise-border" aria-hidden="true" />
                  {NAV_GROUPS.map((group) => {
                    const items = group.items.filter((it) => mobileOverflow.some((m) => m.to === it.to))
                    if (!items.length) return null
                    return (
                      <div key={group.headingKey} className="space-y-1">
                        <p className="select-none px-3 pb-1 pt-1.5 font-mono text-eyebrow uppercase leading-4 text-enterprise-subtle">{tr(group.headingKey)}</p>
                        {items.map((n) => (
                          <NavLink
                            key={n.to}
                            to={n.to}
                            onClick={() => setMobileMoreOpen(false)}
                            className={({ isActive }) =>
                              cn(
                                'flex h-10 items-center border px-3 text-xs font-medium tracking-wide motion-transition-fast focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-enterprise-indigo focus-visible:ring-offset-2 focus-visible:ring-offset-enterprise-bg',
                                isActive ? 'border-enterprise-indigo bg-enterprise-raised text-enterprise-indigo' : 'border-enterprise-border bg-enterprise-bg text-enterprise-text hover:border-enterprise-dim',
                              )
                            }
                          >
                            {tr(n.labelKey)}
                          </NavLink>
                        ))}
                      </div>
                    )
                  })}
                  {/* 主题/语言在手机上进“更多”菜单 — 头部放不下，但移动端不能没有切换入口。 */}
                  <div className="h-px bg-enterprise-border" aria-hidden="true" />
                  <div className="flex items-center justify-between gap-2 px-2 py-1.5">
                    <span className="shrink-0 text-xs tracking-wide text-enterprise-muted">{t('common.theme.label')}</span>
                    <ThemeToggle />
                  </div>
                  <div className="flex items-center justify-between gap-2 px-2 py-1.5">
                    <span className="shrink-0 text-xs tracking-wide text-enterprise-muted">{t('common.language', { defaultValue: 'Language' })}</span>
                    <LanguageSwitch />
                  </div>
                </div>
              )}
            </div>
          )}

        </div>
      </nav>
    </div>
  )
}
